import React, { useEffect, Fragment, memo } from 'react';
import { Route } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce/lib';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import styled from 'styled-components/macro';
import { Helmet } from 'react-helmet';
import {
  userManagementPath,
  goalsPath,
  settingsPath,
  apiManagementPath,
  schedulerPath,
  workingHoursPath,
} from 'contracts/constants';
import config from 'config/config';
import AccountManagement from '../AccountManagement/AccountManagementContainer';
import ApiManagement from '../ApiManagement/ApiManagementContainer';
import Goals from '../Goals/GoalsContainer';
import PbiReportsPage from '../PbiReportsPage/PbiReportsPageContainer';
import ShellHeader from './ShellHeaderContainer';
import ProtectedRoute from '../ProtectedRoute/ProtectedRoute';
import { PermissionAuth0 } from 'models/Permissions';
import CookiesPrompt from '../_common/CookiesPrompt';
import { ShellProps } from './ShellContainer';
import { userRolesToPermissions } from 'helpers/conversions';
import { getAppUserAgreementVersionEnv } from 'helpers/envGetters';
import { TermsModal } from 'components/TermsModal/TermsModal';
import Settings from 'components/Settings/SettingsContainer';
import { KonnectAnalytics } from '../../KonnectAnalytics/konnectAnalytics';
import LinearProgress from 'components/_common/LinearProgress';
import Scheduler from '../Scheduler/SchedulerContainer';
import { setSentryUser } from 'services/sentry';
import WorkingHoursContainer from '../WorkingHours/WorkingHoursContainer';

const AGREEMENT_VERSION = getAppUserAgreementVersionEnv();

const ShellContainer = styled.div`
  height: 100vh;
  max-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled.div`
  flex-grow: 1;
  display: flex;
  max-height: 100%;
  width: 100%;
  overflow: auto;
  @media (max-width: 380px) {
    padding: 0;
  }
  scrollbar-width: thin;
  scrollbar-color: #c1baba;

  &::-webkit-scrollbar-track {
    background: #e3e0e0;
    border-radius: 10px;
  }
  &::-webkit-scrollbar {
    width: 7px;
    height: 7px;
  }
  &::-webkit-scrollbar-thumb {
    background: #c1baba;
    border-radius: 10px;
  }
`;

const Shell = memo((props: ShellProps) => {
  const {
    authService,
    userAgreementVersion,
    userAgreement,
    selectedSystemGroup,
    importedTenantId,
    tenantName,
    accountType,
    office,
    userSystemGroup,
    setAppUserParams,
    updateLogoutTimer,
    setAppUserAgreementVersion,
    setAppUserAgreement,
    setUserEmail,
    setUserLastName,
    setUserFirstName,
    getPortalApiUrl,
    getAppUserSettings,
    getProductionHoursMatrix,
    setScreenSize,
  } = props;
  const { t, i18n } = useTranslation();
  const { idTokenPayload, accessTokenPayload } = authService;
  const isSuperUser = idTokenPayload[`${config.AUTH0_CLAIMS_NAMESPACE}is_super_user`];

  const updateDimensions = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    setScreenSize({ width, height });
  };

  const [debouncedUpdateDimensions] = useDebouncedCallback(e => {
    //@ts-ignore
    const { innerHeight, innerWidth } = e.target;
    setScreenSize({ width: innerWidth, height: innerHeight });
  }, 400);
  // Get user settings
  useEffect(() => {
    updateDimensions();
    getAppUserSettings();
    // Tenant-wide, site-agnostic — fetched once at startup and reused on Settings/Overview/WorkingHours.
    getProductionHoursMatrix();
  }, [getAppUserSettings, getProductionHoursMatrix]);

  // Analytics: send login when required user data is available.
  useEffect(() => {
    const isAuthenticated = authService.isAuthenticated();
    if (!isAuthenticated) {
      return;
    }
    const email = accessTokenPayload?.[`${config.AUTH0_CLAIMS_NAMESPACE}original_user_email`];
    if (
      idTokenPayload &&
      idTokenPayload.sub &&
      email &&
      importedTenantId &&
      tenantName &&
      userSystemGroup &&
      userSystemGroup.length > 0
    ) {
      KonnectAnalytics.login({
        email,
        importedTenantId,
        tenantName,
        accountType,
        office,
        userSystemGroup,
        isSuperUser: isSuperUser,
        config: config,
      });
    }
  }, [
    authService,
    idTokenPayload,
    accessTokenPayload,
    importedTenantId,
    tenantName,
    userSystemGroup,
    accountType,
    office,
    isSuperUser,
  ]);
  // Analytics: update Mixpanel People profile when we have tenant data.
  // This can re-run as optional fields arrive (e.g. office/accountType).
  useEffect(() => {
    if (!authService.isAuthenticated()) return;
    if (!importedTenantId || !tenantName || !userSystemGroup || userSystemGroup.length === 0) {
      return;
    }
    KonnectAnalytics.people.set({
      tenantName,
      importedTenantId,
      office,
      accountType,
      userSystemGroup: userSystemGroup.join(' / '),
      isSuperUser: isSuperUser ? 'true' : 'false',
    });
  }, [
    authService,
    importedTenantId,
    tenantName,
    userSystemGroup,
    office,
    accountType,
    isSuperUser,
  ]);
  useEffect(() => {
    if (authService.isAuthenticated()) {
      // Get current user data: ID from token & user_picture url from API
      if (idTokenPayload && idTokenPayload.sub) {
        setAppUserParams({
          id: idTokenPayload.sub,
          reportTypes: [],
          profilePictureUrl:
            idTokenPayload[`${config.AUTH0_CLAIMS_NAMESPACE}picture`] || idTokenPayload.picture,
          permissions: userRolesToPermissions(
            idTokenPayload[`${config.AUTH0_CLAIMS_NAMESPACE}roles`] || []
          ),
          userAgreementVersion:
            // JSON.parse(localStorage.getItem(`${idTokenPayload.sub}agreement_version`)!) ||
            idTokenPayload[`${config.AUTH0_CLAIMS_NAMESPACE}agreement_version`],
          userAgreement:
            // JSON.parse(localStorage.getItem(`${idTokenPayload.sub}agreement_approved`)!) ||
            idTokenPayload[`${config.AUTH0_CLAIMS_NAMESPACE}agreement_approved`],
        });
        const userEmail = accessTokenPayload[`${config.AUTH0_CLAIMS_NAMESPACE}original_user_email`];
        setUserEmail({
          email: userEmail,
        });
        setUserFirstName({ firstName: idTokenPayload.given_name });
        setUserLastName({ lastName: idTokenPayload.family_name });
        setSentryUser(idTokenPayload.sub, userEmail);

        getPortalApiUrl();
      }

      // Set current locale according to token locale
      const localePropName = `${config.AUTH0_CLAIMS_NAMESPACE}locale`;
      const locale = idTokenPayload && idTokenPayload[localePropName];
      if (locale && i18n.language !== locale) i18n.changeLanguage(locale);
    }
  }, [setAppUserParams, i18n, getPortalApiUrl]);

  useEffect(() => {
    document.addEventListener('click', () => updateLogoutTimer());

    window.addEventListener('resize', e => {
      debouncedUpdateDimensions(e);
    });

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        KonnectAnalytics.startSession('StartSession', { reason: 'user is back on tab' });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('resize', updateDimensions);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [updateLogoutTimer]);

  const onSetAppUserAgreementVersion = () => {
    return setAppUserAgreementVersion(AGREEMENT_VERSION);
  };
  const onSetAppUserAgreement = (isApproved: boolean) => {
    setAppUserAgreement(isApproved, AGREEMENT_VERSION);
  };

  return selectedSystemGroup !== '' ? (
    <ShellContainer>
      <Helmet title={t('title')} />

      <ShellHeader />

      <Fragment>
        {!userAgreement && !isSuperUser && <TermsModal action={onSetAppUserAgreement} />}
        <ContentContainer>
          <Route component={PbiReportsPage} />

          <ProtectedRoute
            exact
            allowedRoles={[PermissionAuth0.Admin]}
            path={userManagementPath}
            component={AccountManagement}
          />

          <ProtectedRoute
            exact
            allowedRoles={[PermissionAuth0.GoalsSetter]}
            path={goalsPath}
            component={Goals}
          />

          <ProtectedRoute
            exact
            allowedRoles={[PermissionAuth0.Certificate]}
            path={apiManagementPath}
            component={ApiManagement}
          />

          <ProtectedRoute exact path={schedulerPath} component={Scheduler} />
          <ProtectedRoute exact path={settingsPath} component={Settings} />
          <ProtectedRoute exact path={workingHoursPath} component={WorkingHoursContainer} />
        </ContentContainer>

        {!(userAgreement && userAgreementVersion === String(AGREEMENT_VERSION)) && !isSuperUser && (
          <CookiesPrompt action={onSetAppUserAgreementVersion} />
        )}
      </Fragment>
    </ShellContainer>
  ) : (
    <LinearProgress></LinearProgress>
  );
});

export default Shell;
